import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";

const emptyTier = {
  key: "", name: "", days: 7, amount: 499, tagline: "",
  perks: [], highlight: false, enabled: true, sort_order: 0,
};

export default function PackagesAdmin() {
  const [tiers, setTiers] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | tier object

  const load = () => api.get("/admin/sponsor-tiers").then((r) => setTiers(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (key) => {
    if (!window.confirm("Delete this package?")) return;
    await api.delete(`/admin/sponsor-tiers/${key}`);
    toast.success("Package removed");
    await load();
  };

  const toggle = async (t, enabled) => {
    await api.patch(`/admin/sponsor-tiers/${t.key}`, { ...t, enabled });
    await load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-black">Sponsor Packages</h1>
          <p className="text-gray-500 mt-1">Design your boost tiers — pricing, duration and perks are all yours.</p>
        </div>
        <Button
          data-testid="new-package-btn"
          onClick={() => setEditing({ ...emptyTier })}
          className="h-11 rounded-full bg-[#FA003F] hover:bg-[#C40031] font-bold px-5"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New Package
        </Button>
      </div>

      {editing && (
        <PackageEditor
          tier={editing}
          isNew={!editing.key}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {tiers.map((t) => (
          <div
            key={t.key}
            data-testid={`package-card-${t.key}`}
            className={`relative rounded-2xl p-6 border-2 bg-white ${
              t.highlight ? "border-[#FA003F]" : "border-black/10"
            }`}
          >
            {t.highlight && (
              <span className="absolute -top-3 left-6 bg-[#FA003F] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                Highlighted
              </span>
            )}
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-gray-500">{t.tagline || "Sponsor tier"}</div>
                <div className="font-display text-2xl font-black mt-1">{t.name}</div>
                <div className="text-xs text-gray-400 mt-0.5">key: {t.key}</div>
              </div>
              <Switch
                data-testid={`package-toggle-${t.key}`}
                checked={t.enabled}
                onCheckedChange={(v) => toggle(t, v)}
                className="data-[state=checked]:bg-[#FA003F]"
              />
            </div>
            <div className="flex items-baseline gap-3 mt-4">
              <div className="font-display text-4xl font-black text-[#FA003F]">₹{Number(t.amount).toLocaleString()}</div>
              <div className="text-sm text-gray-500">for {t.days} days</div>
            </div>
            {t.perks?.length > 0 && (
              <ul className="mt-4 space-y-1.5 text-sm text-gray-700">
                {t.perks.map((p, i) => (
                  <li key={i} className="flex gap-2"><span className="text-[#FA003F] font-black">·</span> {p}</li>
                ))}
              </ul>
            )}
            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                data-testid={`edit-package-${t.key}`}
                onClick={() => setEditing({ ...t })}
                className="rounded-full h-9"
              >
                <Pencil className="h-4 w-4 mr-1" /> Edit
              </Button>
              <Button
                variant="outline"
                data-testid={`delete-package-${t.key}`}
                onClick={() => remove(t.key)}
                className="rounded-full h-9 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
        {tiers.length === 0 && (
          <div className="col-span-2 bg-white rounded-2xl border border-dashed border-black/20 p-12 text-center text-gray-500">
            No packages yet. Click "New Package" to create one.
          </div>
        )}
      </div>
    </div>
  );
}

function PackageEditor({ tier, isNew, onSaved, onCancel }) {
  const [form, setForm] = useState(tier);
  const [perkText, setPerkText] = useState(tier.perks?.join("\n") || "");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name || !form.days || form.amount == null) {
      toast.error("Name, price, and days are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        days: Number(form.days),
        amount: Number(form.amount),
        sort_order: Number(form.sort_order || 0),
        perks: perkText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (isNew) await api.post("/admin/sponsor-tiers", payload);
      else await api.patch(`/admin/sponsor-tiers/${form.key}`, payload);
      toast.success(isNew ? "Package created" : "Package updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-black/10 rounded-2xl p-6 mb-4" data-testid="package-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-black text-xl">{isNew ? "Create package" : `Edit ${form.name}`}</h3>
        <button onClick={onCancel} className="h-9 w-9 rounded-full border border-black/10 grid place-items-center hover:bg-black hover:text-white transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Package name">
          <Input data-testid="pkg-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-xl" />
        </Field>
        <Field label="Tagline">
          <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Great for one-off events" className="h-11 rounded-xl" />
        </Field>
        <Field label="Price (₹)">
          <Input data-testid="pkg-amount" type="number" min={0} value={form.amount} onChange={(e) => set("amount", e.target.value)} className="h-11 rounded-xl" />
        </Field>
        <Field label="Duration (days)">
          <Input data-testid="pkg-days" type="number" min={1} value={form.days} onChange={(e) => set("days", e.target.value)} className="h-11 rounded-xl" />
        </Field>
        <Field label="Sort order">
          <Input type="number" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className="h-11 rounded-xl" />
        </Field>
        {isNew && (
          <Field label="Key (auto if blank)">
            <Input value={form.key} onChange={(e) => set("key", e.target.value)} placeholder="e.g. boost_7d" className="h-11 rounded-xl" />
          </Field>
        )}
      </div>
      <div className="mt-4">
        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Perks (one per line)</Label>
        <Textarea
          data-testid="pkg-perks"
          value={perkText}
          onChange={(e) => setPerkText(e.target.value)}
          placeholder="Pinned in featured hero&#10;Sponsored badge on card"
          className="rounded-xl min-h-32 mt-1.5"
        />
      </div>
      <div className="flex items-center gap-6 mt-4">
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="pkg-enabled" checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} className="data-[state=checked]:bg-[#FA003F]" />
          Enabled
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="pkg-highlight" checked={form.highlight} onCheckedChange={(v) => set("highlight", v)} className="data-[state=checked]:bg-[#FA003F]" />
          Highlight as "Best value"
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={onCancel} className="rounded-full h-11 px-6">Cancel</Button>
        <Button data-testid="pkg-save-btn" onClick={save} disabled={saving} className="rounded-full h-11 px-6 bg-[#FA003F] hover:bg-[#C40031]">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</Label>{children}</div>;
}
